import {
  ArrowDown,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Church,
  CircleX,
} from "lucide-react";

import type { PlatformDashboardFunnelItem } from "@/api/platform";

interface OnboardingFunnelCardProps {
  stages: PlatformDashboardFunnelItem[];
}

const funnelOrder = [
  "SUBMITTED",
  "UNDER_REVIEW",
  "APPROVED",
  "CONVERTED",
  "DECLINED",
];

const funnelLabels: Record<string, string> = {
  SUBMITTED: "Submitted",
  UNDER_REVIEW: "Under Review",
  APPROVED: "Approved",
  CONVERTED: "Converted",
  DECLINED: "Declined",
};

const funnelDescriptions: Record<string, string> = {
  SUBMITTED: "New pilot access requests",
  UNDER_REVIEW: "Requests being evaluated",
  APPROVED: "Approved for the pilot",
  CONVERTED: "Converted into churches",
  DECLINED: "Requests not approved",
};

const funnelStyles: Record<
  string,
  {
    containerClassName: string;
    iconClassName: string;
    icon: typeof Clock3;
  }
> = {
  SUBMITTED: {
    containerClassName: "border-indigo-200 bg-indigo-50",
    iconClassName: "bg-indigo-100 text-indigo-700",
    icon: Clock3,
  },
  UNDER_REVIEW: {
    containerClassName: "border-amber-200 bg-amber-50",
    iconClassName: "bg-amber-100 text-amber-700",
    icon: ClipboardCheck,
  },
  APPROVED: {
    containerClassName: "border-sky-200 bg-sky-50",
    iconClassName: "bg-sky-100 text-sky-700",
    icon: CheckCircle2,
  },
  CONVERTED: {
    containerClassName: "border-emerald-200 bg-emerald-50",
    iconClassName: "bg-emerald-100 text-emerald-700",
    icon: Church,
  },
  DECLINED: {
    containerClassName: "border-rose-200 bg-rose-50",
    iconClassName: "bg-rose-100 text-rose-700",
    icon: CircleX,
  },
};

export function OnboardingFunnelCard({
  stages,
}: OnboardingFunnelCardProps) {
  const stageMap = new Map(
    stages.map((stage) => [stage.status, stage.count])
  );

  const orderedStages = funnelOrder.map((status) => ({
    status,
    count: stageMap.get(status) ?? 0,
  }));

  const submitted = stageMap.get("SUBMITTED") ?? 0;
  const underReview = stageMap.get("UNDER_REVIEW") ?? 0;
  const approved = stageMap.get("APPROVED") ?? 0;
  const converted = stageMap.get("CONVERTED") ?? 0;

  const totalRequests = stages.reduce(
    (total, stage) => total + stage.count,
    0
  );

  const approvalBase =
    submitted + underReview + approved + converted;

  const approvalRate =
    approvalBase > 0
      ? Math.round(
          ((approved + converted) / approvalBase) * 100
        )
      : 0;

  const conversionBase = approved + converted;

  const conversionRate =
    conversionBase > 0
      ? Math.round((converted / conversionBase) * 100)
      : 0;

  const awaitingAction = submitted + underReview;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div>
        <h2 className="text-lg font-semibold text-slate-950">
          Onboarding funnel
        </h2>

        <p className="mt-1 text-sm text-slate-500">
          Pilot requests moving through the review and conversion process.
        </p>
      </div>

      <div className="mt-6 space-y-3">
        {orderedStages.map((stage, index) => (
          <div key={stage.status}>
            <FunnelStage
              status={stage.status}
              count={stage.count}
            />

            {index < orderedStages.length - 1 && (
              <div className="flex justify-center py-2 text-slate-300">
                <ArrowDown className="h-4 w-4" />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <Metric
          label="Total requests"
          value={totalRequests.toLocaleString()}
        />

        <Metric
          label="Approval rate"
          value={`${approvalRate}%`}
        />

        <Metric
          label="Conversion rate"
          value={`${conversionRate}%`}
        />
      </div>

      <div className="mt-4 rounded-xl bg-slate-50 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Awaiting action
        </p>

        <p className="mt-1 text-sm font-semibold text-slate-900">
          {awaitingAction.toLocaleString()}{" "}
          {awaitingAction === 1 ? "request" : "requests"}
        </p>
      </div>
    </section>
  );
}

interface FunnelStageProps {
  status: string;
  count: number;
}

function FunnelStage({
  status,
  count,
}: FunnelStageProps) {
  const style =
    funnelStyles[status] ??
    funnelStyles.SUBMITTED;

  const Icon = style.icon;

  return (
    <div
      className={[
        "flex items-center justify-between gap-4 rounded-xl border p-4",
        style.containerClassName,
      ].join(" ")}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div
          className={[
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
            style.iconClassName,
          ].join(" ")}
        >
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">
            {funnelLabels[status] ?? formatStatus(status)}
          </p>

          <p className="mt-0.5 text-xs text-slate-500">
            {funnelDescriptions[status] ??
              "Pilot request status"}
          </p>
        </div>
      </div>

      <p className="text-2xl font-bold tracking-tight text-slate-950">
        {count.toLocaleString()}
      </p>
    </div>
  );
}

interface MetricProps {
  label: string;
  value: string;
}

function Metric({
  label,
  value,
}: MetricProps) {
  return (
    <div className="rounded-xl bg-slate-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>

      <p className="mt-2 text-xl font-bold text-slate-950">
        {value}
      </p>
    </div>
  );
}

function formatStatus(value: string): string {
  return value
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map(
      (part) =>
        part.charAt(0).toUpperCase() +
        part.slice(1)
    )
    .join(" ");
}